import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const SVG_WIDTH = 700;
const SVG_HEIGHT = 500;
const PANEL_COUNT = 4;
const MARGIN = { top: 40, right: 30, bottom: 50, left: 70 };
const PANEL_GAP = 8;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '12px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '2px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '0',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

function formatDateFull(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

const PANELS = [
  { key: 'observed', label: 'Observed', color: '#2563eb', fillColor: '#2563eb', fillOpacity: 0.1, strokeWidth: 1.5, type: 'area' },
  { key: 'trend', label: 'Trend', color: '#166534', fillColor: null, fillOpacity: 0, strokeWidth: 2.5, type: 'line' },
  { key: 'seasonal', label: 'Seasonal', color: '#ea580c', fillColor: '#ea580c', fillOpacity: 0.12, strokeWidth: 1.5, type: 'zero-area' },
  { key: 'residual', label: 'Residual', color: '#6b7280', fillColor: null, fillOpacity: 0, strokeWidth: 1, type: 'bars' },
];

export default function SeasonalDecomposition({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const chartData = useMemo(() => {
    if (!data || !data.dates || data.dates.length === 0) return null;
    const n = data.dates.length;
    const parsed = [];
    for (let i = 0; i < n; i++) {
      parsed.push({
        date: new Date(data.dates[i]),
        dateStr: data.dates[i],
        observed: data.observed ? data.observed[i] : null,
        trend: data.trend ? data.trend[i] : null,
        seasonal: data.seasonal ? data.seasonal[i] : null,
        residual: data.residual ? data.residual[i] : null,
      });
    }
    return parsed;
  }, [data]);

  useEffect(() => {
    if (!chartData || chartData.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : SVG_WIDTH;
    const width = Math.max(SVG_WIDTH, containerWidth);

    svg
      .attr('viewBox', `0 0 ${width} ${SVG_HEIGHT}`)
      .attr('width', '100%')
      .attr('height', SVG_HEIGHT)
      .style('overflow', 'visible');

    const innerW = width - MARGIN.left - MARGIN.right;
    const totalPanelSpace = SVG_HEIGHT - MARGIN.top - MARGIN.bottom;
    const panelH = (totalPanelSpace - PANEL_GAP * (PANEL_COUNT - 1)) / PANEL_COUNT;

    const n = chartData.length;
    const parsedDates = chartData.map((d) => d.date);

    // Shared x scale
    const x = d3.scaleTime()
      .domain(d3.extent(parsedDates))
      .range([0, innerW]);

    // Build per-panel y scales
    const yScales = {};
    PANELS.forEach((panel) => {
      const vals = chartData.map((d) => d[panel.key]).filter((v) => v != null);
      if (vals.length === 0) {
        yScales[panel.key] = d3.scaleLinear().domain([0, 1]).range([panelH, 0]);
        return;
      }
      let lo = d3.min(vals);
      let hi = d3.max(vals);
      // For seasonal and residual, ensure zero is included
      if (panel.type === 'zero-area' || panel.type === 'bars') {
        lo = Math.min(lo, 0);
        hi = Math.max(hi, 0);
      }
      const pad = (hi - lo) * 0.1 || 1;
      yScales[panel.key] = d3.scaleLinear()
        .domain([lo - pad, hi + pad])
        .nice()
        .range([panelH, 0]);
    });

    // Draw each panel
    PANELS.forEach((panel, panelIndex) => {
      const panelY = MARGIN.top + panelIndex * (panelH + PANEL_GAP);
      const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${panelY})`);
      const y = yScales[panel.key];

      // Subtle gridlines
      const yTicks = y.ticks(4);
      g.selectAll('.grid-line')
        .data(yTicks)
        .enter()
        .append('line')
        .attr('x1', 0)
        .attr('x2', innerW)
        .attr('y1', (d) => y(d))
        .attr('y2', (d) => y(d))
        .attr('stroke', '#f3f4f6')
        .attr('stroke-width', 1);

      // Panel background border (subtle)
      g.append('rect')
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', innerW)
        .attr('height', panelH)
        .attr('fill', 'none')
        .attr('stroke', '#f3f4f6')
        .attr('stroke-width', 1);

      // Y axis
      g.append('g')
        .call(
          d3.axisLeft(y)
            .ticks(4)
            .tickSize(0)
            .tickFormat((v) => {
              if (panel.key === 'observed' || panel.key === 'trend') {
                return `$${v.toFixed(0)}`;
              }
              return v.toFixed(2);
            })
        )
        .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
        .selectAll('text')
        .attr('font-size', '9px')
        .attr('fill', '#6b7280');

      // Y axis label
      g.append('text')
        .attr('transform', 'rotate(-90)')
        .attr('x', -panelH / 2)
        .attr('y', -50)
        .attr('text-anchor', 'middle')
        .attr('font-size', '10px')
        .attr('font-weight', 600)
        .attr('fill', '#374151')
        .text(panel.label);

      // X axis — only on the bottom panel
      if (panelIndex === PANEL_COUNT - 1) {
        g.append('g')
          .attr('transform', `translate(0, ${panelH})`)
          .call(
            d3.axisBottom(x)
              .ticks(Math.min(n, 10))
              .tickSize(0)
              .tickFormat((d) => formatDateLabel(d.toISOString()))
          )
          .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
          .selectAll('text')
          .attr('font-size', '10px')
          .attr('fill', '#6b7280')
          .attr('transform', 'rotate(-30)')
          .attr('text-anchor', 'end')
          .attr('dx', '-0.5em')
          .attr('dy', '0.25em');
      }

      // Draw data
      const validData = chartData.filter((d) => d[panel.key] != null);

      if (panel.type === 'area') {
        // Observed: line + area fill
        const area = d3.area()
          .x((d) => x(d.date))
          .y0(panelH)
          .y1((d) => y(d[panel.key]))
          .curve(d3.curveMonotoneX);

        g.append('path')
          .datum(validData)
          .attr('d', area)
          .attr('fill', panel.fillColor)
          .attr('fill-opacity', panel.fillOpacity)
          .attr('stroke', 'none');

        const line = d3.line()
          .x((d) => x(d.date))
          .y((d) => y(d[panel.key]))
          .curve(d3.curveMonotoneX);

        g.append('path')
          .datum(validData)
          .attr('d', line)
          .attr('fill', 'none')
          .attr('stroke', panel.color)
          .attr('stroke-width', panel.strokeWidth);
      } else if (panel.type === 'line') {
        // Trend: thick line only
        const line = d3.line()
          .x((d) => x(d.date))
          .y((d) => y(d[panel.key]))
          .curve(d3.curveMonotoneX);

        g.append('path')
          .datum(validData)
          .attr('d', line)
          .attr('fill', 'none')
          .attr('stroke', panel.color)
          .attr('stroke-width', panel.strokeWidth);
      } else if (panel.type === 'zero-area') {
        // Seasonal: line + fill to zero
        const zeroY = y(0);

        const area = d3.area()
          .x((d) => x(d.date))
          .y0(zeroY)
          .y1((d) => y(d[panel.key]))
          .curve(d3.curveMonotoneX);

        g.append('path')
          .datum(validData)
          .attr('d', area)
          .attr('fill', panel.fillColor)
          .attr('fill-opacity', panel.fillOpacity)
          .attr('stroke', 'none');

        // Zero reference line
        g.append('line')
          .attr('x1', 0)
          .attr('x2', innerW)
          .attr('y1', zeroY)
          .attr('y2', zeroY)
          .attr('stroke', '#d1d5db')
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '4,3');

        const line = d3.line()
          .x((d) => x(d.date))
          .y((d) => y(d[panel.key]))
          .curve(d3.curveMonotoneX);

        g.append('path')
          .datum(validData)
          .attr('d', line)
          .attr('fill', 'none')
          .attr('stroke', panel.color)
          .attr('stroke-width', panel.strokeWidth);
      } else if (panel.type === 'bars') {
        // Residual: thin bars from zero line
        const zeroY = y(0);
        const barWidth = Math.max(1, Math.min(4, innerW / n - 1));

        // Zero reference line
        g.append('line')
          .attr('x1', 0)
          .attr('x2', innerW)
          .attr('y1', zeroY)
          .attr('y2', zeroY)
          .attr('stroke', '#d1d5db')
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '4,3');

        validData.forEach((d) => {
          const val = d[panel.key];
          const cx = x(d.date);
          const cy = y(val);
          const barH = Math.abs(cy - zeroY);

          g.append('rect')
            .attr('x', cx - barWidth / 2)
            .attr('y', val >= 0 ? cy : zeroY)
            .attr('width', barWidth)
            .attr('height', Math.max(0.5, barH))
            .attr('fill', panel.color)
            .attr('fill-opacity', 0.5);
        });
      }
    });

    // ── Crosshair overlay across all panels ──
    const crosshairGroup = svg.append('g').attr('class', 'crosshair-group');

    // Vertical crosshair lines for each panel
    const crosshairLines = PANELS.map((_, panelIndex) => {
      const panelY = MARGIN.top + panelIndex * (panelH + PANEL_GAP);
      return crosshairGroup.append('line')
        .attr('y1', panelY)
        .attr('y2', panelY + panelH)
        .attr('stroke', '#d1d5db')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '3,3')
        .style('display', 'none');
    });

    // Highlight dots on each panel
    const highlightDots = PANELS.map((panel) => {
      return crosshairGroup.append('circle')
        .attr('r', 3)
        .attr('fill', '#fff')
        .attr('stroke', panel.color)
        .attr('stroke-width', 2)
        .style('display', 'none');
    });

    // Invisible overlay rect for mouse events spanning all panels
    svg.append('rect')
      .attr('x', MARGIN.left)
      .attr('y', MARGIN.top)
      .attr('width', innerW)
      .attr('height', totalPanelSpace)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair')
      .on('mousemove', (event) => {
        const [mx] = d3.pointer(event);
        const adjustedX = mx - MARGIN.left;
        const dateAtMouse = x.invert(adjustedX);

        // Bisect to find nearest date index
        const bisect = d3.bisector((d) => d.date).left;
        let idx = bisect(chartData, dateAtMouse);
        if (idx > 0 && idx < n) {
          const d0 = chartData[idx - 1].date;
          const d1 = chartData[idx].date;
          idx = (dateAtMouse - d0) > (d1 - dateAtMouse) ? idx : idx - 1;
        }
        idx = Math.max(0, Math.min(n - 1, idx));

        const cx = MARGIN.left + x(chartData[idx].date);

        // Update crosshair lines
        crosshairLines.forEach((line) => {
          line.attr('x1', cx).attr('x2', cx).style('display', null);
        });

        // Update highlight dots
        PANELS.forEach((panel, panelIndex) => {
          const val = chartData[idx][panel.key];
          if (val != null) {
            const panelY = MARGIN.top + panelIndex * (panelH + PANEL_GAP);
            const dotY = panelY + yScales[panel.key](val);
            highlightDots[panelIndex]
              .attr('cx', cx)
              .attr('cy', dotY)
              .style('display', null);
          } else {
            highlightDots[panelIndex].style('display', 'none');
          }
        });

        setTooltip({
          x: event.clientX + 12,
          y: event.clientY - 10,
          date: chartData[idx].dateStr,
          observed: chartData[idx].observed,
          trend: chartData[idx].trend,
          seasonal: chartData[idx].seasonal,
          residual: chartData[idx].residual,
        });
      })
      .on('mouseleave', () => {
        crosshairLines.forEach((line) => line.style('display', 'none'));
        highlightDots.forEach((dot) => dot.style('display', 'none'));
        setTooltip(null);
      });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [chartData]);

  // Handle window resize
  useEffect(() => {
    if (!chartData || chartData.length === 0) return;

    let resizeTimer;
    const debouncedResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();
        setTooltip(null);
      }, 150);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', debouncedResize);
    };
  }, [chartData]);

  // Empty state
  if (!data || !data.dates || data.dates.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Price Trend Decomposition</div>
          <div style={styles.subtitle}>
            Separating observed prices into trend, seasonal, and residual components
          </div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No data available
        </div>
      </div>
    );
  }

  const fieldLabel = data.fieldLabel || 'Price';

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Price Trend Decomposition</div>
        <div style={styles.subtitle}>
          Separating observed prices into trend, seasonal, and residual components
          {fieldLabel !== 'Price' ? ` — ${fieldLabel}` : ''}
        </div>
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#2563eb" strokeWidth="1.5" />
          </svg>
          <span>Observed</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#166534" strokeWidth="2.5" />
          </svg>
          <span>Trend</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <rect x="0" y="0" width="20" height="10" fill="#ea580c" fillOpacity="0.12" rx="2" />
            <line x1="0" y1="5" x2="20" y2="5" stroke="#ea580c" strokeWidth="1.5" />
          </svg>
          <span>Seasonal</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <rect x="4" y="2" width="3" height="6" fill="#6b7280" fillOpacity="0.5" />
            <rect x="9" y="4" width="3" height="4" fill="#6b7280" fillOpacity="0.5" />
            <rect x="14" y="1" width="3" height="7" fill="#6b7280" fillOpacity="0.5" />
          </svg>
          <span>Residual</span>
        </div>
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {formatDateFull(tooltip.date)}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Observed</span>
            <span style={{ fontWeight: 600, color: '#2563eb' }}>
              {tooltip.observed != null ? `$${tooltip.observed.toFixed(2)}` : 'N/A'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Trend</span>
            <span style={{ fontWeight: 600, color: '#166534' }}>
              {tooltip.trend != null ? `$${tooltip.trend.toFixed(2)}` : 'N/A'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Seasonal</span>
            <span style={{ fontWeight: 600, color: '#ea580c' }}>
              {tooltip.seasonal != null
                ? `${tooltip.seasonal >= 0 ? '+' : ''}${tooltip.seasonal.toFixed(2)}`
                : 'N/A'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
            <span style={{ color: '#6b7280' }}>Residual</span>
            <span style={{ fontWeight: 600, color: '#6b7280' }}>
              {tooltip.residual != null
                ? `${tooltip.residual >= 0 ? '+' : ''}${tooltip.residual.toFixed(2)}`
                : 'N/A'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
